import MDBox from 'components/MDBox'
import Switch from '@mui/material/Switch'
import { useAppServices } from 'hook/services'
import { useEffect, useState, useRef } from 'react'
import EditModal from '../../EditModal'
import toast from 'react-hot-toast'

export default function useAppData() {
  const AppService = useAppServices()
  const [list, setList] = useState([])
  const [loader, setLoader] = useState(true)
  const [search, setSearch] = useState('')
  const typingTimeoutRef = useRef(null)

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 0,
    totalUsers: 0,
  })

  const [dateRange, setDateRange] = useState({
    startDate: null,
    endDate: null,
  })

  const getAppTypes = async (page = 1, limit = 10, searchTerm = search) => {
    setLoader(true)
    try {
      const params = {
        page,
        limit,
        toaster: false,
        startDate: dateRange.startDate ? dateRange.startDate.toISOString() : 'null',
        endDate: dateRange.endDate ? dateRange.endDate.toISOString() : 'null',
        search: searchTerm?.trim() || '',
      }

      const query = Object.entries(params)
        .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
        .join('&')

      const { response } = await AppService.listing.get({ query })

      if (response?.success) {
        setList(response.data || [])
        setPagination({
          page: response.pagination?.currentPage || page,
          limit: response.pagination?.usersPerPage || limit,
          totalPages: response.pagination?.totalPages || 0,
          totalUsers: response.pagination?.totalUsers || 0,
        })
      } else {
        setList([])
      }
    } catch (err) {
      console.error('❌ Error fetching listings:', err)
      setList([])
    } finally {
      setLoader(false)
    }
  }

  const onLoad = () => {
    getAppTypes(pagination.page, pagination.limit)
  }

  const handleToggleHome = async (id, current) => {
    setList((prev) =>
      prev.map((item) => (item._id === id ? { ...item, showOnHome: !current } : item))
    )

    const { response, error } = await AppService.listing.toggleHome({
      payload: { id, showOnHome: !current },
    })

    if (!response?.success) {
      setList((prev) =>
        prev.map((item) => (item._id === id ? { ...item, showOnHome: current } : item))
      )
      console.error('Toggle failed:', error)
    }
  }


  const handleToggleChristmasStyled = async (id, current) => {
    setList((prev) =>
      prev.map((item) => (item._id === id ? { ...item, isChristmasStyled: !current } : item))
    )

    const { response, error } = await AppService.listing.toggleChristmasStyled({
      payload: { id },
    })

    if (!response?.success) {
      setList((prev) =>
        prev.map((item) => (item._id === id ? { ...item, isChristmasStyled: current } : item))
      )
      console.error('Toggle Christmas-styled failed:', error)
    }
  }

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this listing?')) return
    try {
      const { response, error } = await AppService.listing.delete({ payload: { _id: id } })
      if (response?.success) {
        toast.success('Listing deleted successfully!')
        getAppTypes(pagination.page, pagination.limit)
      } else {
        toast.error(response?.message || 'Failed to delete listing')
        console.error('Delete failed:', error)
      }
    } catch (err) {
      console.error('Error deleting listing:', err)
      toast.error('Something went wrong while deleting listing')
    }
  }

  useEffect(() => {
    getAppTypes(1, pagination.limit)
  }, [dateRange])

  useEffect(() => {
    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current)
    typingTimeoutRef.current = setTimeout(() => {
      getAppTypes(1, pagination.limit, search)
    }, 1000)

    return () => clearTimeout(typingTimeoutRef.current)
  }, [search])

  const handlePageChange = (newPage) => {
    getAppTypes(newPage, pagination.limit, search)
  }

  const handleLimitChange = (newLimit) => {
    getAppTypes(1, newLimit, search)
  }

  const handleSearch = () => {
    getAppTypes(1, pagination.limit, search)
  }

  const truncateText = (text, wordLimit = 8) => {
    if (!text) return ''
    const words = text.split(' ')
    return words.length > wordLimit ? words.slice(0, wordLimit).join(' ') + '...' : text
  }


  const handleTogglePrivate = async (id, current) => {
    setList((prev) =>
      prev.map((item) => (item._id === id ? { ...item, isPrivate: !current } : item))
    )

    const { response, error } = await AppService.listing.togglePrivate({
      payload: { listing_id: id },
    })

    if (response?.success) {
      toast.success(
        response.isPrivate
          ? 'Listing is now Private (link-only)'
          : 'Listing is now Public'
      )
    } else {
      setList((prev) =>
        prev.map((item) => (item._id === id ? { ...item, isPrivate: current } : item))
      )
      toast.error(response?.message || 'Failed to update visibility')
      console.error('Toggle private failed:', error)
    }
  }


  return {
    loader,
    handleRefresh: onLoad,
    setDateRange,
    dateRange,
    pagination,
    handlePageChange,
    handleLimitChange,
    search,
    setSearch,
    handleSearch,
    dataTableData: {
      columns: [
        { Header: 'UserName', accessor: 'username' },
        { Header: 'Email', accessor: 'email' },
        { Header: 'Title', accessor: 'title' },
        { Header: 'Status', accessor: 'status' },
        { Header: 'Description', accessor: 'listing_description' },
        { Header: 'Show On Home', accessor: 'showOnHome' },
        { Header: 'Christmas Styled', accessor: 'isChristmasStyled' },
        { Header: 'Private', accessor: 'isPrivate' },
        { Header: 'Action', accessor: 'action' },
      ],
      rows: list.map((data) => ({
        username: data?.user_id?.username || '',
        email: data?.user_id?.email || '',
        title: data.title ? truncateText(data.title, 4) : '',
        status: data?.status,
        listing_description: data?.listing_description
          ? truncateText(data?.listing_description.replace(/<[^>]+>/g, ''), 10)
          : '',
        showOnHome: (
          <Switch
            checked={!!data?.showOnHome}
            onChange={() => handleToggleHome(data._id, data.showOnHome)}
            color="primary"
          />
        ),
        isChristmasStyled: (
          <Switch
            checked={!!data?.isChristmasStyled}
            onChange={() => handleToggleChristmasStyled(data._id, data.isChristmasStyled)}
            color="success"
          />
        ),
        isPrivate: (
          <Switch
            checked={!!data?.isPrivate}
            onChange={() => handleTogglePrivate(data._id, data.isPrivate)}
            color="warning"
          />
        ),
        action: (
          <MDBox display="flex" gap={1}>
            <EditModal data={data} handleRefresh={onLoad} />
            <button
              onClick={() => handleDelete(data._id)}
              style={{
                background: '#DC2626',
                color: 'white',
                padding: '6px 12px',
                borderRadius: '6px',
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Delete
            </button>
          </MDBox>
        ),
      })),
    },
  }
}
